import DialogCreateClient from "../../../../components/Dialog/CreateClient";
import DialogUploadClients from "../../../../components/Dialog/UploadClients";
import TableSettings from "../../../../components/Table/Settings";
import { ColumnLabel } from "../../../../components/Table/Settings/Toolbar";
import { Dialog, Button, Notification } from "@rio-cloud/rio-uikit";
import { createClient, deleteClient, deleteClientsBatch, editClient, getClients } from "../../../../services/form";
import { useEffect, useState } from "react";
import { Client } from "../../../../types";
import { formatDocument } from "../../../../utils/validation/cnpjMask";

const defaultColumnOrder = ["document", "name"];

const columnLabels: ColumnLabel = {
  id: "1",
  document: "CNPJ / CPF",
  name: "Nome",
  date: "",
};

interface IClientTableItem {
  id: number;
  document: string;
  name: string;
  date: string;
}

interface IClientSelected {
  id: number;
  document: string;
  name: string;
}

const ClientsPage = () => {
  const [createNewModal, setCreateNewModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteBatchModal, setDeleteBatchModal] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [rawClients, setRawClients] = useState<Client[]>([]);
  const [options, setOptions] = useState<IClientTableItem[]>([]);
  const [optionSelected, setOptionSelected] = useState<IClientSelected | null>(null);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);

  const mountOption = (data: Client[]): IClientTableItem[] => {
    return (data || []).map((res) => ({
      id: res.id,
      document: formatDocument(res.document),
      name: res.name || "-",
      date: "",
    }));
  };

  const fetchOptions = async () => {
    try {
      const response = await getClients();
      setRawClients(response || []);
      setOptions(mountOption(response));
    } catch (error) {
      console.error("Error fetching clients", error);
      setRawClients([]);
      setOptions([]);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  const handleSaveNewOption = async ({ document, name }: { document: string; name: string }) => {
    try {
      await createClient({
        document,
        name: name || undefined,
      });
      fetchOptions();
      setCreateNewModal(false);
      Notification.success("Cliente criado com sucesso.");
    } catch (error: any) {
      if (error?.response?.status === 409) {
        Notification.error("Já existe um cliente cadastrado com este CNPJ/CPF nesta conta.");
      } else {
        const message = error?.response?.data?.message || "Não foi possível criar o cliente no momento.";
        Notification.error(message);
      }
    }
  };

  const handleEditOption = async ({ document, name }: { document: string; name: string }) => {
    if (!optionSelected?.id) return;
    try {
      await editClient({
        id: optionSelected.id,
        document,
        name: name || undefined,
      });
      fetchOptions();
      setEditModal(false);
      Notification.success("Cliente alterado com sucesso.");
    } catch (error: any) {
      if (error?.response?.status === 409) {
        Notification.error("Já existe um cliente cadastrado com este CNPJ/CPF nesta conta.");
      } else {
        const message = error?.response?.data?.message || "Não foi possível alterar o cliente no momento.";
        Notification.error(message);
      }
    }
  };

  const handleDeleteOption = async () => {
    if (!optionSelected?.id) return;
    try {
      await deleteClient(optionSelected.id);
      fetchOptions();
      setSelectedClientIds((prev) => prev.filter((id) => id !== optionSelected.id));
      setDeleteModal(false);
      Notification.success("Cliente excluído com sucesso.");
    } catch (error: any) {
      const message = error?.response?.data?.message || "Não foi possível excluir o cliente no momento.";
      Notification.error(message);
    }
  };

  const handleDeleteBatch = async () => {
    if (selectedClientIds.length === 0) return;
    try {
      const response = await deleteClientsBatch(selectedClientIds);
      Notification.success(`${response.data.deletedCount} cliente(s) excluído(s) com sucesso.`);
      setSelectedClientIds([]);
      setDeleteBatchModal(false);
      fetchOptions();
    } catch (error: any) {
      const message = error?.response?.data?.message || "Não foi possível excluir os clientes selecionados.";
      Notification.error(message);
    }
  };

  const handleToggleSelectRow = (id: number) => {
    setSelectedClientIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedClientIds(options.map((opt) => opt.id));
    } else {
      setSelectedClientIds([]);
    }
  };

  const extraToolbarActions = (
    <div className="display-flex gap-10 align-items-center">
      <Button
        bsStyle={Button.DEFAULT}
        iconName="rioglyph-upload"
        className="rounded-small width-170"
        onClick={() => setUploadModal(true)}
      >
        Importar Planilha
      </Button>
      {selectedClientIds.length > 0 && (
        <Button
          bsStyle={Button.DANGER}
          iconName="rioglyph-trash"
          className="rounded-small width-190"
          onClick={() => setDeleteBatchModal(true)}
        >
          Excluir Selecionados ({selectedClientIds.length})
        </Button>
      )}
    </div>
  );

  return (
    <>
      <TableSettings
        data={options}
        totalData={options.length}
        columnLabels={columnLabels}
        defaultColumnOrder={defaultColumnOrder}
        viewType="TABLE"
        onLoadMore={() => {}}
        handleRefresh={fetchOptions}
        handleDownload={() => {}}
        openModal={() => {
          setCreateNewModal(true);
        }}
        onEdit={(id) => {
          const client = rawClients.find((c) => c.id === id);
          if (client) {
            setOptionSelected({
              id: client.id,
              document: client.document,
              name: client.name || "",
            });
            setEditModal(true);
          }
        }}
        onDelete={(id) => {
          const client = rawClients.find((c) => c.id === id);
          if (client) {
            setOptionSelected({
              id: client.id,
              document: client.document,
              name: client.name || "",
            });
            setDeleteModal(true);
          }
        }}
        onDeleteFile={() => {}}
        onGetFile={() => {}}
        onObservation={() => {}}
        selectable={true}
        selectedIds={selectedClientIds}
        onToggleSelectRow={handleToggleSelectRow}
        onToggleSelectAll={handleToggleSelectAll}
        extraToolbarActions={extraToolbarActions}
      />

      <DialogCreateClient
        title="Novo Cliente"
        show={createNewModal}
        onClose={() => setCreateNewModal(false)}
        onSave={handleSaveNewOption}
      />

      <DialogCreateClient
        title="Editar Cliente"
        show={editModal}
        onClose={() => setEditModal(false)}
        onSave={handleEditOption}
        editDocument={optionSelected?.document}
        editName={optionSelected?.name}
      />

      <DialogUploadClients
        show={uploadModal}
        onClose={() => setUploadModal(false)}
        onSuccess={() => {
          fetchOptions();
          setSelectedClientIds([]);
        }}
      />

      <Dialog
        show={deleteModal}
        title={<span className="text-medium text-size-16">Excluir cliente</span>}
        body={
          <div className="padding-20">
            <p>
              Tem certeza que deseja excluir o cliente{" "}
              <strong>{optionSelected?.name || optionSelected?.document}</strong>?
            </p>
            <p className="text-color-danger">Esta ação não pode ser desfeita.</p>
          </div>
        }
        footer={
          <div className="display-flex align-items-center justify-content-end gap-10">
            <Button onClick={() => setDeleteModal(false)} className="width-150 rounded-small">
              Cancelar
            </Button>
            <Button onClick={handleDeleteOption} bsStyle={Button.DANGER} className="width-150 rounded-small">
              Excluir
            </Button>
          </div>
        }
        onClose={() => setDeleteModal(false)}
        showCloseButton
      />

      <Dialog
        show={deleteBatchModal}
        title={<span className="text-medium text-size-16">Excluir clientes selecionados</span>}
        body={
          <div className="padding-20">
            <p>
              Tem certeza que deseja excluir os{" "}
              <strong>{selectedClientIds.length}</strong> clientes selecionados?
            </p>
            <p className="text-color-danger">Esta ação não pode ser desfeita.</p>
          </div>
        }
        footer={
          <div className="display-flex align-items-center justify-content-end gap-10">
            <Button onClick={() => setDeleteBatchModal(false)} className="width-150 rounded-small">
              Cancelar
            </Button>
            <Button onClick={handleDeleteBatch} bsStyle={Button.DANGER} className="width-150 rounded-small">
              Excluir Selecionados
            </Button>
          </div>
        }
        onClose={() => setDeleteBatchModal(false)}
        showCloseButton
      />
    </>
  );
};

export default ClientsPage;
